import type { PointerEvent } from "react";

/**
 * Whether the device's PRIMARY input can hover (a mouse or trackpad). False on a phone or tablet, where the
 * shared tooltip opens from a tap rather than a hover or a long press (PlainTooltip). Read once: a device's
 * primary input does not change mid-session.
 */
export const PRIMARY_INPUT_CAN_HOVER =
  typeof globalThis.matchMedia === "function" && globalThis.matchMedia("(hover: hover)").matches;

/**
 * Hover is a mouse/pen concept. A tap swaps the toolbar icon for its highlighted version under the
 * finger, and the touch's "leave" then lands on the removed element, so a highlight set on touch sticks.
 */
export const isHoverPointer = (event: Pick<PointerEvent, "pointerType">): boolean => event.pointerType !== "touch";

/**
 * What a tap operates: native controls and the ARIA widget roles. One list for the tooltip's tap rule
 * (`revealsOnTap`), the drag cancel below and the mobile layout checker's targets, so they cannot disagree. A
 * bare `a`, not `a[href]`: MUI's Link without an href is an `<a>` with only an onClick.
 */
export const CONTROL_SELECTOR = [
  "a",
  "button",
  'input:not([type="hidden"])',
  "select",
  "textarea",
  "summary",
  '[contenteditable]:not([contenteditable="false"])',
  '[role="button"]',
  '[role="link"]',
  '[role="tab"]',
  '[role="menuitem"]',
  '[role="menuitemcheckbox"]',
  '[role="menuitemradio"]',
  '[role="option"]',
  '[role="checkbox"]',
  '[role="radio"]',
  '[role="switch"]',
  '[role="slider"]',
  '[role="spinbutton"]',
  '[role="combobox"]',
  '[role="treeitem"]',
].join(", ");

/** On an element that is a button only so a tooltip can be focused and tapped (IconComponent's tooltip-only icon). */
export const TOOLTIP_ONLY_ATTR = "data-tooltip-only";
/** Set by the shared tooltip, on a touch-only device, on an anchor a tap can open it from. */
export const TAP_TOOLTIP_ATTR = "data-tap-tooltip";
/**
 * On a tooltip anchor whose tap must reveal the tooltip even inside something that acts on a tap: an info icon in
 * an accordion's summary, the wrapper of a disabled control. On a touch-only device the tap then stops at the
 * anchor, so the container does not act as well; a hover opens the tooltip as it always did.
 */
export const TAP_REVEAL_ATTR = "data-tap-reveal";
/**
 * On the box a disabled control's tooltip anchors on (IconComponent, DisabledTooltip), since the disabled control
 * takes no pointer events: still part of the control, so a press there never starts a panel drag.
 */
export const DISABLED_CONTROL_ATTR = "data-disabled-control";
/** On a grid's root while its rows act on a click (SlickGrid's own canvas listener, which no DOM check sees). */
export const ROW_ACTION_ATTR = "data-row-action";

/**
 * Controls inside a draggable panel that must not start a drag. react-draggable calls preventDefault on
 * every touchstart it handles, which cancels the tap's click, so on a phone a control this does not match
 * cannot be pressed. The panel still drags from its padding. A label activates its control; a tap-tooltip
 * anchor opens its tooltip with a tap. `.e-input-group` (Syncfusion), `.MuiFormControl-root` and
 * `.MuiSlider-root` (MUI) are widgets whose own padding, label or rail is part of the control rather than of
 * the panel.
 */
export const DRAG_CANCEL_SELECTOR = [
  CONTROL_SELECTOR,
  "label",
  `[${TAP_TOOLTIP_ATTR}]`,
  `[${DISABLED_CONTROL_ATTR}]`,
  ".e-input-group",
  ".MuiFormControl-root",
  ".MuiSlider-root",
].join(", ");

/** The two computed properties `revealsOnTap` reads; injected by tests, since jsdom computes no cursor or position. */
export type TapStyle = Pick<CSSStyleDeclaration, "cursor" | "position">;

const isTagged = (element: Element, tag: string): boolean => element.tagName === tag;

/** A label's own control, when it has one: a tap on the label activates it (`HTMLLabelElement.control`). */
const labelledControl = (element: Element): Element | null =>
  element instanceof HTMLLabelElement ? element.control : null;

/**
 * Whether React renders into `element`: the app's root, or a portal's container (a grid cell, a dock panel). React
 * gives each of them a no-op `onclick`, as it does an element with an onClick, and marks it with a key of its own
 * (`_reactListening…`) when it starts listening there.
 */
const isReactContainer = (element: Element): boolean =>
  Object.keys(element).some((key) => key.startsWith("_reactListening"));

/**
 * Whether a tap on `element` itself does something: it is an enabled control (CONTROL_SELECTOR) or it has a click
 * handler, unless it is marked tooltip-only. For a tooltip's anchor, which `revealsOnTap` does not judge: a
 * component can render a link or a button that its props do not show.
 */
export function actsOnTap(element: Element): boolean {
  if (element.hasAttribute(TOOLTIP_ONLY_ATTR) || element.matches(':disabled, [aria-disabled="true"]')) return false;
  if (element.matches(CONTROL_SELECTOR)) return true;
  const onclick: unknown = Reflect.get(element, "onclick");
  return typeof onclick === "function" && !isReactContainer(element);
}

/**
 * Whether a tap at `target` does nothing but reveal the tooltip of `anchor` (the element the tooltip is
 * anchored to, `target` or an ancestor of it): no control, label, click handler, clickable row or clickable
 * container takes the same tap. Walked from the target up to the body:
 * - an element marked tooltip-only (TOOLTIP_ONLY_ATTR) is skipped;
 * - any other enabled control (CONTROL_SELECTOR), or a row of a grid whose rows act (ROW_ACTION_ATTR), takes it;
 * - a label takes it when it has a control of its own, unless the tap already passed a tooltip-only button;
 * - a `position: fixed` element ends the walk: what is above it (a dialog's backdrop) is not under the finger;
 * - an element with a React onClick takes it: React gives every host element that has one an `onclick`. Not a
 *   container React renders into, which gets one too, nor `.MuiInputBase-root`, which focuses its input only when
 *   it is the target itself, nor rc-dock's `.dock-panel`, which only moves focus to its tab bar;
 * - above the anchor, an element that sets `cursor: pointer` (its parent's cursor is not) takes it: a clickable
 *   card or row. Grid rows and cells are left to ROW_ACTION_ATTR (a design-system skin points every row).
 * The anchor itself is not judged here: the tooltip does that, from its child element and with actsOnTap.
 */
export function revealsOnTap(
  target: Element,
  anchor: Element,
  styleOf: (element: Element) => TapStyle = (element) => getComputedStyle(element),
): boolean {
  let passedTooltipOnlyButton = false;
  let aboveAnchor = false;
  for (let element: Element | null = target; element && !isTagged(element, "BODY"); element = element.parentElement) {
    const isAnchor = element === anchor;
    if (element.hasAttribute(TOOLTIP_ONLY_ATTR)) {
      if (isTagged(element, "BUTTON")) passedTooltipOnlyButton = true;
    } else {
      const enabled = !element.matches(':disabled, [aria-disabled="true"]');
      if (!isAnchor && enabled && element.matches(CONTROL_SELECTOR)) return false;
      if (element.matches(`[${ROW_ACTION_ATTR}] .slick-row`)) return false;
      const control = labelledControl(element);
      if (control && !control.hasAttribute(TOOLTIP_ONLY_ATTR) && !passedTooltipOnlyButton) return false;
      const style = styleOf(element);
      if (style.position === "fixed") return true;
      const onclick: unknown = Reflect.get(element, "onclick");
      if (
        !isAnchor &&
        typeof onclick === "function" &&
        !element.matches(".MuiInputBase-root, .dock-panel") &&
        !isReactContainer(element)
      ) {
        return false;
      }
      const parent = element.parentElement;
      if (
        aboveAnchor &&
        style.cursor === "pointer" &&
        !element.matches(".slick-row, .slick-cell") &&
        (!parent || styleOf(parent).cursor !== "pointer")
      ) {
        return false;
      }
    }
    if (isAnchor) aboveAnchor = true;
  }
  return true;
}
